import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api-client';
import { canSearchPatientLookup, canSearchPatientUhid } from '../../lib/patient-uhid-search';
import {
  addEmgRegistrationPayment,
  addEmgRegistrationRefund,
  cancelEmgRegistrationBill,
  createEmgRegistration,
  fetchEmgLookups,
  fetchEmgPrescription,
  fetchEmgRegistration,
  fetchEmgRegistrations,
  saveEmgPrescription,
  updateEmgRegistration,
  updateEmgRegistrationPayment,
} from './emg.api';
import type { EmgListFilters, EmgPayment, UpdateEmgRegistrationPayload, UpsertEmgPrescriptionPayload } from './types';

export const useEmgRegistrations = (filters: EmgListFilters, enabled = true) => useQuery({ queryKey: ['emg', 'registrations', filters], queryFn: () => fetchEmgRegistrations(filters), enabled });
export const useEmgPatientBills = (uhid: string | null, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
  useEmgRegistrations({ page: 1, limit: 200, field: 'uhid', value: uhid ?? '', ...(sortBy ? { sortBy, sortOrder } : {}) }, Boolean(uhid));
export const useEmgRegistration = (id?: string) => useQuery({ queryKey: ['emg', 'registration', id], queryFn: () => fetchEmgRegistration(id!), enabled: Boolean(id) });
export const useEmgPrescription = (registrationId?: string) => useQuery({
  queryKey: ['emg', 'prescription', registrationId],
  queryFn: () => fetchEmgPrescription(registrationId!),
  enabled: Boolean(registrationId),
  retry: (failureCount, error) => error instanceof ApiError && error.status === 404 ? false : failureCount < 2,
});
export const useEmgLookup = (type: string, search = '', state = '') => {
  const patientLookup = type === 'patients' || type.startsWith('appointment-patients');
  return useQuery({
    queryKey: ['emg', 'lookup', type, search, state],
    queryFn: () => fetchEmgLookups(type, search, state),
    enabled: !patientLookup || (type === 'patients' ? canSearchPatientUhid(search) : canSearchPatientLookup(search)),
    ...(patientLookup ? { staleTime: 0, refetchOnMount: 'always' as const } : { staleTime: 60_000 }),
  });
};

export const useCreateEmgRegistration = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createEmgRegistration, onSuccess: () => void client.invalidateQueries({ queryKey: ['emg'] }) });
};

export const useUpdateEmgRegistration = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEmgRegistrationPayload }) => updateEmgRegistration(id, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['emg'] }),
  });
};

export const useUpdateEmgRegistrationPayment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentIndex, payload }: { id: string; paymentIndex: number; payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number } }) =>
      updateEmgRegistrationPayment(id, paymentIndex, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['emg'] }),
  });
};

export const useAddEmgRegistrationPayment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number } }) =>
      addEmgRegistrationPayment(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['emg'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

export const useAddEmgRegistrationRefund = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number } }) =>
      addEmgRegistrationRefund(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['emg'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

export const useSaveEmgPrescription = (registrationId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertEmgPrescriptionPayload) => saveEmgPrescription(registrationId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['emg'] }),
  });
};

export const useCancelEmgRegistrationBill = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelEmgRegistrationBill(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['emg'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};
